import type { DiagramPitch } from '@ukinfo/shared';
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '../ui';
import {
  MAX_ZOOM,
  MIN_ZOOM,
  clampNumber,
  formatZoom,
  fullView,
  isZoomed,
  nextZoomStep,
  screenToDrawing,
  viewAround,
  viewBoxOf,
  zoomAround,
  type DiagramView,
  type DrawingPoint,
  type ScreenRect,
} from './diagramView';

/**
 * Zoom und Schwenken einer Skizze - gemeinsam fuer den Editor und die Vollbildansicht.
 *
 * Die Ansicht ist reiner Zustand der Oberflaeche (nicht Teil der Skizze, kein
 * Rueckgaengig-Schritt). Der Haken liefert die `viewBox` des Ausschnitts, die Zoomknoepfe
 * und die Gesten:
 *
 * - Zwei Finger: Auseinander-/Zusammenziehen zoomt um den Mittelpunkt der Finger,
 *   gemeinsames Verschieben schwenkt. Der zweite Finger ruft `onGestureStart` - der
 *   Editor bricht damit seine laufende Ein-Finger-Aktion ab. Bis alle Finger oben sind,
 *   gehoert jede Beruehrung der Geste (kein Setzen, Zeichnen oder Schwenken nebenbei).
 * - Mausrad mit Strg (und Trackpad-Pinch, der als Rad mit `ctrlKey` ankommt) zoomt um die
 *   Mausposition.
 * - Schwenken mit einem Zeiger: `panStart`/`panMove` fuer den Editor (Ziehen auf leerer
 *   Flaeche), `panHandlers` fuer Ansichten ohne eigene Bedienung.
 *
 * Alle Rechnungen stehen in `diagramView.ts`.
 */

const ZERO_RECT: ScreenRect = { left: 0, top: 0, width: 0, height: 0 };

/** Mausrad-Empfindlichkeit: ein Rastschritt (100) gibt etwa 1,5-fachen Zoom, der Trackpad-Pinch feine Schritte. */
const WHEEL_SENSITIVITY = 0.004;

interface Pinch {
  ids: [number, number];
  startDistance: number;
  startZoom: number;
  /** Zeichnungspunkt unter der Mitte der Finger zu Beginn - er bleibt unter den Fingern. */
  point: DrawingPoint;
}

/** Der angefasste Zeichnungspunkt beim Schwenken mit einem Zeiger. */
export interface PanGrab {
  point: DrawingPoint;
}

export interface DiagramViewport {
  view: DiagramView;
  zoom: number;
  zoomed: boolean;
  /** `viewBox` des SVG: ohne Zoom die ganze Skizze, sonst der Ausschnitt. */
  viewBox: string;
  canZoomIn: boolean;
  canZoomOut: boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  /** "Ganzes Feld": zurueck auf 1x. */
  fit: () => void;
  /** Ref-Funktion fuer das SVG: merkt es sich und haengt das Mausrad (Strg) daran. */
  svgRef: (element: SVGSVGElement | null) => void;
  /** Das gebundene SVG. */
  element: { readonly current: SVGSVGElement | null };
  /** Ausgabe von `getBoundingClientRect` des SVG als schlichtes Rechteck. */
  rect: () => ScreenRect;
  /** Der Ausschnitt jetzt - auch mitten in einer Geste aktuell (der State laeuft einen Takt nach). */
  current: () => DiagramView;
  /** Zwei-Finger-Geste; `true` = die Beruehrung gehoert der Geste, der Aufrufer ignoriert sie. */
  gestureDown: (event: ReactPointerEvent<SVGSVGElement>) => boolean;
  gestureMove: (event: ReactPointerEvent<SVGSVGElement>) => boolean;
  gestureUp: (event: ReactPointerEvent<SVGSVGElement>) => boolean;
  panStart: (clientX: number, clientY: number) => PanGrab | null;
  panMove: (grab: PanGrab, clientX: number, clientY: number) => void;
  /** Fertige Zeigerbehandlung fuer Ansichten ohne eigene Bedienung: Gesten und, wenn gezoomt, Schwenken mit einem Zeiger. */
  panHandlers: {
    onPointerDown: (event: ReactPointerEvent<SVGSVGElement>) => void;
    onPointerMove: (event: ReactPointerEvent<SVGSVGElement>) => void;
    onPointerUp: (event: ReactPointerEvent<SVGSVGElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<SVGSVGElement>) => void;
  };
}

export function useDiagramViewport({ pitch, onGestureStart }: { pitch: DiagramPitch; onGestureStart?: () => void }): DiagramViewport {
  const [view, setViewState] = useState<DiagramView>(() => fullView(pitch));
  const viewRef = useRef(view);
  const pitchRef = useRef(pitch);
  pitchRef.current = pitch;
  const startRef = useRef(onGestureStart);
  startRef.current = onGestureStart;
  const elementRef = useRef<SVGSVGElement | null>(null);

  /** Beruehrungen (Finger, Stift), die gerade aufliegen. */
  const pointers = useRef(new Map<number, DrawingPoint>());
  const gestureActive = useRef(false);
  const pinch = useRef<Pinch | null>(null);
  const panning = useRef<{ pointerId: number; grab: PanGrab } | null>(null);

  const setView = useCallback((next: DiagramView): void => {
    viewRef.current = next;
    setViewState(next);
  }, []);

  const rect = useCallback((): ScreenRect => {
    const box = elementRef.current?.getBoundingClientRect();
    return box ? { left: box.left, top: box.top, width: box.width, height: box.height } : ZERO_RECT;
  }, []);

  // Ein anderes Feld beginnt wieder mit dem ganzen Feld.
  const lastPitch = useRef(pitch);
  useEffect(() => {
    if (lastPitch.current === pitch) return;
    lastPitch.current = pitch;
    setView(fullView(pitch));
  }, [pitch, setView]);

  /* ------------------------------------------------------------ Knoepfe, Mausrad */

  const zoomTo = useCallback(
    (zoom: number, client?: DrawingPoint): void => {
      setView(zoomAround(pitchRef.current, rect(), viewRef.current, zoom, client));
    },
    [rect, setView],
  );
  const zoomIn = useCallback((): void => zoomTo(nextZoomStep(viewRef.current.zoom, 1)), [zoomTo]);
  const zoomOut = useCallback((): void => zoomTo(nextZoomStep(viewRef.current.zoom, -1)), [zoomTo]);
  const fit = useCallback((): void => setView(fullView(pitchRef.current)), [setView]);

  const wheelHandler = useRef<((event: WheelEvent) => void) | null>(null);
  wheelHandler.current = (event: WheelEvent): void => {
    // Ohne Strg scrollt die Seite wie gewohnt.
    if (!event.ctrlKey) return;
    event.preventDefault();
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 100 : 1;
    const zoom = clampNumber(viewRef.current.zoom * Math.exp(-event.deltaY * unit * WHEEL_SENSITIVITY), MIN_ZOOM, MAX_ZOOM);
    zoomTo(zoom, { x: event.clientX, y: event.clientY });
  };

  const unbindWheel = useRef<(() => void) | null>(null);
  const svgRef = useCallback((element: SVGSVGElement | null): void => {
    unbindWheel.current?.();
    unbindWheel.current = null;
    elementRef.current = element;
    if (!element) return;
    const listener = (event: WheelEvent): void => wheelHandler.current?.(event);
    // Nicht passiv, damit der Browser bei Strg+Rad nicht die ganze Seite zoomt.
    element.addEventListener('wheel', listener, { passive: false });
    unbindWheel.current = () => element.removeEventListener('wheel', listener);
  }, []);

  /* --------------------------------------------------------------------- Gesten */

  const capture = (event: ReactPointerEvent<SVGSVGElement>): void => {
    try {
      elementRef.current?.setPointerCapture(event.pointerId);
    } catch {
      // Ohne Zeigererfassung laufen die Ereignisse weiter, solange der Finger ueber dem Feld bleibt.
    }
  };
  const release = (event: ReactPointerEvent<SVGSVGElement>): void => {
    try {
      elementRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      // Die Erfassung war schon beendet.
    }
  };

  const beginPinch = (): void => {
    const [first, second] = [...pointers.current.entries()];
    if (!first || !second) return;
    const middle = { x: (first[1].x + second[1].x) / 2, y: (first[1].y + second[1].y) / 2 };
    pinch.current = {
      ids: [first[0], second[0]],
      startDistance: Math.max(1, Math.hypot(second[1].x - first[1].x, second[1].y - first[1].y)),
      startZoom: viewRef.current.zoom,
      point: screenToDrawing(middle.x, middle.y, rect(), pitchRef.current, viewRef.current) ?? { x: 50, y: 40 },
    };
  };

  const gestureDown = (event: ReactPointerEvent<SVGSVGElement>): boolean => {
    // Der erste Finger einer neuen Beruehrung beginnt frisch - ein verpasstes "Hoch" hinterlaesst nichts.
    if (event.isPrimary === true) {
      pointers.current.clear();
      gestureActive.current = false;
      pinch.current = null;
    }
    if (event.pointerType === 'mouse') return false;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (gestureActive.current) {
      capture(event);
      return true;
    }
    if (pointers.current.size < 2) return false;
    gestureActive.current = true;
    panning.current = null;
    capture(event);
    beginPinch();
    startRef.current?.();
    return true;
  };

  const gestureMove = (event: ReactPointerEvent<SVGSVGElement>): boolean => {
    if (pointers.current.has(event.pointerId)) pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (!gestureActive.current) return false;
    const state = pinch.current;
    if (!state || !state.ids.includes(event.pointerId)) return true;
    const a = pointers.current.get(state.ids[0]);
    const b = pointers.current.get(state.ids[1]);
    if (!a || !b) return true;
    const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const zoom = clampNumber(state.startZoom * (Math.hypot(b.x - a.x, b.y - a.y) / state.startDistance), MIN_ZOOM, MAX_ZOOM);
    setView(viewAround(pitchRef.current, rect(), zoom, state.point, middle));
    return true;
  };

  const gestureUp = (event: ReactPointerEvent<SVGSVGElement>): boolean => {
    pointers.current.delete(event.pointerId);
    if (!gestureActive.current) return false;
    release(event);
    // Hebt einer der beiden Finger ab, endet die Geste; die uebrigen bleiben ohne Wirkung bis zum Ende.
    if (pinch.current?.ids.includes(event.pointerId)) pinch.current = null;
    if (pointers.current.size === 0) gestureActive.current = false;
    return true;
  };

  /* ------------------------------------------------------------------- Schwenken */

  const panStart = useCallback(
    (clientX: number, clientY: number): PanGrab | null => {
      const point = screenToDrawing(clientX, clientY, rect(), pitchRef.current, viewRef.current);
      return point ? { point } : null;
    },
    [rect],
  );
  const panMove = useCallback(
    (grab: PanGrab, clientX: number, clientY: number): void => {
      setView(viewAround(pitchRef.current, rect(), viewRef.current.zoom, grab.point, { x: clientX, y: clientY }));
    },
    [rect, setView],
  );

  const endPan = (event: ReactPointerEvent<SVGSVGElement>): void => {
    if (gestureUp(event)) return;
    if (panning.current?.pointerId !== event.pointerId) return;
    panning.current = null;
    release(event);
  };
  const panHandlers: DiagramViewport['panHandlers'] = {
    onPointerDown: (event) => {
      if (gestureDown(event)) return;
      if (!isZoomed(viewRef.current) || panning.current) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const grab = panStart(event.clientX, event.clientY);
      if (!grab) return;
      panning.current = { pointerId: event.pointerId, grab };
      capture(event);
    },
    onPointerMove: (event) => {
      if (gestureMove(event)) return;
      const state = panning.current;
      if (state && state.pointerId === event.pointerId) panMove(state.grab, event.clientX, event.clientY);
    },
    onPointerUp: endPan,
    onPointerCancel: endPan,
  };

  return {
    view,
    zoom: view.zoom,
    zoomed: isZoomed(view),
    viewBox: viewBoxOf(pitch, view),
    canZoomIn: view.zoom < MAX_ZOOM - 0.01,
    canZoomOut: view.zoom > MIN_ZOOM + 0.01,
    zoomIn,
    zoomOut,
    fit,
    svgRef,
    element: elementRef,
    rect,
    current: () => viewRef.current,
    gestureDown,
    gestureMove,
    gestureUp,
    panStart,
    panMove,
    panHandlers,
  };
}

/* ------------------------------------------------------------------ Bedienung */

function ZoomIcon({ id }: { id: 'in' | 'out' | 'fit' }): JSX.Element {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
      {id === 'fit' ? (
        <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" {...stroke} />
      ) : (
        <>
          <circle cx="10" cy="10" r="6" {...stroke} />
          <path d="M15 15l5 5" {...stroke} />
          <path d={id === 'in' ? 'M7 10h6M10 7v6' : 'M7 10h6'} {...stroke} />
        </>
      )}
    </svg>
  );
}

function ZoomButton({ label, icon, disabled, onClick }: { label: string; icon: 'in' | 'out' | 'fit'; disabled: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 shadow-sm transition-colors hover:bg-slate-50',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-45',
      )}
    >
      <ZoomIcon id={icon} />
    </button>
  );
}

/** Zoomknoepfe samt Anzeige der Stufe: Lupe plus, Lupe minus, ganzes Feld. */
export function ZoomControls({ viewport, className }: { viewport: DiagramViewport; className?: string }): JSX.Element {
  return (
    <div role="toolbar" aria-label="Zoom" data-testid="zoom-controls" className={cn('flex items-center gap-1.5', className)}>
      <ZoomButton label="Verkleinern" icon="out" disabled={!viewport.canZoomOut} onClick={viewport.zoomOut} />
      <span aria-live="polite" data-testid="zoom-level" className="min-w-[3.25rem] text-center text-sm font-medium tabular-nums text-slate-700">
        <span className="sr-only">Zoom </span>
        {formatZoom(viewport.zoom)}
      </span>
      <ZoomButton label="Vergrößern" icon="in" disabled={!viewport.canZoomIn} onClick={viewport.zoomIn} />
      <ZoomButton label="Ganzes Feld" icon="fit" disabled={!viewport.zoomed} onClick={viewport.fit} />
    </div>
  );
}
